import type { Moment } from 'moment';
import { clampToMaxPeriod } from '~utils/date-picker-helpers';
import type { Period } from '../types';

const DATE_FORMAT = 'YYYY-MM-DD';

/** The two period shapes a card can be in. */
export type PeriodMode = 'preset' | 'custom';

/** Result of `describePeriod`: identifies the mode and, for custom periods, the concrete bounds. */
export interface PeriodDescription {
    mode: PeriodMode;
    /** Non-null only when `mode === 'custom'`. */
    bounds: { start: string; end: string } | null;
}

/**
 * Classifies a `Period` value as either a named preset or a custom date range,
 * and extracts the concrete bounds for custom periods.
 *
 * Pure function: no side effects, no date resolution. Preset strings remain
 * unresolved (the caller uses `resolvePeriod` from resolvers.ts for that).
 */
export const describePeriod = (period: Period): PeriodDescription => {
    if (typeof period === 'object') {
        return {
            mode: 'custom',
            bounds: { start: period.start, end: period.end },
        };
    }
    return { mode: 'preset', bounds: null };
};

/** Same cap as the page filter's `setPeriod`, applied to the override's own value. */
export const clampPickedRange = (
    validDates: [Moment, Moment]
): { start: string; end: string } =>
    clampToMaxPeriod({
        start: validDates[0].format(DATE_FORMAT),
        end: validDates[1].format(DATE_FORMAT),
    });
